// =====================================================================
// AccountDialog (Financeiro Tesouraria / S63) — cria/edita uma conta
// (caixa / banco / maquininha). Saldo inicial; conta padrão recebe as baixas.
// =====================================================================
import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  MenuItem, FormControlLabel, Checkbox, Box, CircularProgress
} from "@material-ui/core";
import { toast } from "react-toastify";
import { createAccount, updateAccount, ACCOUNT_TYPES, financeErrorMessage } from "../../services/financeService";
import useIsMobile from "../../utils/useIsMobile";

const AccountDialog = ({ open, account, unitId, onClose, onSaved }) => {
  const isMobile = useIsMobile();
  const editing = !!account;
  const [name, setName] = useState("");
  const [type, setType] = useState("caixa");
  const [openingBalance, setOpeningBalance] = useState("");
  const [bankName, setBankName] = useState("");
  const [agency, setAgency] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(account?.name || "");
      setType(account?.type || "caixa");
      setOpeningBalance(account?.openingBalance != null ? String(account.openingBalance) : "");
      setBankName(account?.bankName || "");
      setAgency(account?.agency || "");
      setAccountNumber(account?.accountNumber || "");
      setIsDefault(!!account?.isDefault);
      setActive(account?.active !== false);
    }
  }, [open, account]);

  const handleSave = async () => {
    if (!name.trim()) { toast.error("Informe o nome da conta."); return; }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        type,
        openingBalance: Number(String(openingBalance).replace(",", ".")) || 0,
        bankName, agency, accountNumber, isDefault
      };
      if (editing) {
        payload.active = active;
        await updateAccount(account.id, payload);
      } else {
        // [S90] unidade ativa (modelo B); ignorado se a company não tem unidades.
        if (unitId != null) payload.unitId = unitId;
        await createAccount(payload);
      }
      toast.success("Conta salva.");
      onSaved && onSaved();
    } catch (err) {
      toast.error(financeErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const showBank = type === "banco" || type === "poupanca";

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth fullScreen={isMobile}>
      <DialogTitle>{editing ? "Editar conta" : "Nova conta"}</DialogTitle>
      <DialogContent>
        <TextField label="Nome" value={name} onChange={(e) => setName(e.target.value)} fullWidth margin="dense" autoFocus
          placeholder="ex.: Caixa, Itaú CC, Maquininha Cielo" />
        <TextField select label="Tipo" value={type} onChange={(e) => setType(e.target.value)} fullWidth margin="dense">
          {ACCOUNT_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
        </TextField>
        <TextField label="Saldo inicial (R$)" type="number" value={openingBalance}
          onChange={(e) => setOpeningBalance(e.target.value)} fullWidth margin="dense" inputProps={{ step: "0.01" }}
          helperText={editing
            ? "Ajustar o saldo inicial recalcula o saldo atual pelo mesmo valor."
            : "Saldo de abertura da conta."} />
        {showBank && (
          <Box display="flex" style={{ gap: 12, flexWrap: "wrap" }}>
            <TextField label="Banco" value={bankName} onChange={(e) => setBankName(e.target.value)} margin="dense" style={{ flexGrow: 1, minWidth: 130 }} />
            <TextField label="Agência" value={agency} onChange={(e) => setAgency(e.target.value)} margin="dense" style={{ flexGrow: 1, minWidth: 130 }} />
            <TextField label="Conta" value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} margin="dense" style={{ flexGrow: 1, minWidth: 130 }} />
          </Box>
        )}
        <FormControlLabel
          control={<Checkbox checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} color="primary" />}
          label="Conta padrão (recebe as baixas por default)" />
        {editing && (
          <FormControlLabel
            control={<Checkbox checked={active} onChange={(e) => setActive(e.target.checked)} color="primary" />}
            label="Ativa" />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : null}>Salvar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AccountDialog;
